import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { pdf } from '@react-pdf/renderer';
import DrePDFDoc from './DrePDF';
import { exportDreReportExcel } from '../../utils/reportExcelExports';
import saleService from '../../services/saleService';
import expenseService from '../../services/expenseService';
import vehicleService from '../../services/vehicleService';
import { cashMovementService } from '../../services/caixaService';
import { Sale, Expense, MovimentacaoCaixa, expenseAffectsStoreCash, movimentacaoAfetaSaldoCaixa, EXPENSE_CATEGORIES, ExpenseCategory } from '../../types';
import {
  getCompanyRevenueFromSale,
  getCashProfitInRangeForSale,
  getCashProfitEventsFromSale,
  sumCashProfitInRange,
  sumCashRevenueInRange,
} from '../../utils/saleRealProfit';

/** Converte qualquer valor para número (evita concatenação de strings nos cálculos). Aceita pt-BR (1.900,00) e formato com ponto (2000.00). */
const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

/** Máscara única para todos os valores em reais (padrão pt-BR). */
const formatCurrency = (value: unknown): string => {
  const n = toNum(value);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/** Valor de dedução (gastos): exibe entre parênteses no mesmo padrão pt-BR. */
const formatCurrencyDeduction = (value: unknown): string =>
  `(${formatCurrency(value)})`;

/** Um item de taxas/impostos cadastrado manualmente para a DRE */
export interface TaxEntry {
  id: string;
  description: string;
  amount: number;
}

/** Resultado da DRE gerada */
export interface DreResult {
  /** Faturamento bruto (soma dos sale_price de todas as vendas no período) */
  faturamentoBrutoVendas: number;
  /** Receita de vendas considerando consignação: consignado = comissão; direto = sale_price (referência; não compõe o total sozinha) */
  receitaVendas: number;
  /** Lucro das vendas reconhecido no período (regime de caixa; parcelas na data da baixa). */
  lucroRealVendas: number;
  receitaMovimentacoes: number;
  /** Lucro real das vendas + entradas de movimentação (base do resultado) */
  totalReceitas: number;
  totalDespesas: number;
  totalSaidasMovimentacoes: number;
  totalGastos: number;
  totalTaxas: number;
  resultado: number;
  /** Quantidade de veículos vendidos com data de venda no período */
  veiculosVendidos: number;
}

export type DreSaleExpenseItem = {
  id: number;
  expense_date: string;
  category: string;
  category_label: string;
  description: string;
  amount: number;
};

export type DreSaleDetailRow = {
  id: number;
  sale_date: string;
  vehicle_id: number;
  vehicle_label: string;
  plate?: string;
  is_consignado: boolean;
  sale_price: number;
  receita_empresa: number;
  custo_compra: number;
  despesas_veiculo: number;
  /** Lucro operacional da venda (receita − custo − despesas do veículo) */
  lucro_operacional: number;
  /** Lucro reconhecido no período (regime de caixa) */
  lucro_estimado: number;
  expense_items: DreSaleExpenseItem[];
};

export type DreExpenseDetailRow = {
  id: number;
  expense_date: string;
  vehicle_label: string;
  category: string;
  description: string;
  amount: number;
  affects_store_cash: boolean;
};

export type DreMovementDetailRow = {
  id: string;
  date: string;
  type: 'entrada' | 'saida';
  category: string;
  description: string;
  amount: number;
  affects_cash: boolean;
  payable_status?: string;
  due_date?: string;
};

const getStartOfMonth = (d: Date) => {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  return x.toISOString().split('T')[0];
};
const getEndOfMonth = (d: Date) => {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return x.toISOString().split('T')[0];
};

const DrePage: React.FC = () => {
  const now = new Date();
  const [dateFrom, setDateFrom] = useState(getStartOfMonth(now));
  const [dateTo, setDateTo] = useState(getEndOfMonth(now));
  const [taxEntries, setTaxEntries] = useState<TaxEntry[]>([]);
  const [dreResult, setDreResult] = useState<DreResult | null>(null);
  const [showDetailed, setShowDetailed] = useState(false);
  const [detailSales, setDetailSales] = useState<DreSaleDetailRow[]>([]);
  const [detailExpenses, setDetailExpenses] = useState<DreExpenseDetailRow[]>([]);
  const [detailMovements, setDetailMovements] = useState<DreMovementDetailRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDateInRange = useCallback((dateStr: string) => {
    const d = dateStr.split('T')[0];
    return d >= dateFrom && d <= dateTo;
  }, [dateFrom, dateTo]);

  const addTaxEntry = () => {
    setTaxEntries((prev) => [
      ...prev,
      { id: `tax-${Date.now()}`, description: '', amount: 0 },
    ]);
  };

  const removeTaxEntry = (id: string) => {
    setTaxEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const updateTaxEntry = (id: string, field: 'description' | 'amount', value: string | number) => {
    setTaxEntries((prev) =>
      prev.map((e) =>
        e.id === id
          ? field === 'description'
            ? { ...e, description: String(value) }
            : { ...e, amount: typeof value === 'number' ? value : parseFloat(String(value).replace(/\s/g, '').replace(/\./g, '').replace(',', '.')) || 0 }
          : e
      )
    );
  };

  const handleGenerate = async () => {
    setError(null);
    setDreResult(null);
    setDetailSales([]);
    setDetailExpenses([]);
    setDetailMovements([]);
    setLoading(true);
    try {
      const [sales, expenses, movimentacoes] = await Promise.all([
        saleService.getAllSales(),
        expenseService.getAllExpenses(),
        cashMovementService.getAll(),
      ]);

      // Garantir que cada venda tenha dados do veículo (para consignação)
      let salesWithVehicle = (sales || []) as Sale[];
      const missingVehicle = salesWithVehicle.some((s: Sale) => s.vehicle_id && !s.vehicle);
      if (missingVehicle) {
        try {
          const vehicles = await vehicleService.getAllVehicles();
          const byId: Record<number, any> = {};
          (vehicles || []).forEach((v: any) => { byId[v.id] = v; });
          salesWithVehicle = salesWithVehicle.map((s: Sale) =>
            s.vehicle ? s : { ...s, vehicle: byId[s.vehicle_id] ?? null }
          );
        } catch (_) {
          // mantém sales sem vehicle; receita = sale_price para todas
        }
      }

      const salesForCash = await saleService.enrichTermSalesWithInstallments(salesWithVehicle);
      const salesFiltered = salesForCash.filter((s: Sale) => isDateInRange(s.sale_date || ''));
      const salesWithCashInPeriod = salesForCash.filter((s: Sale) =>
        getCashProfitEventsFromSale(s).some((event) => isDateInRange(event.date))
      );
      const expensesFiltered = (expenses || []).filter((e: Expense) => isDateInRange(e.expense_date || ''));
      const movFiltered = (movimentacoes || []).filter((m: MovimentacaoCaixa) => isDateInRange(m.date || ''));

      // ===== Detalhamento (lucro por venda, despesas e movimentações) =====
      const vehicleLabelById: Record<number, string> = {};
      const vehiclePlateById: Record<number, string> = {};
      salesWithVehicle.forEach((s: Sale) => {
        if (s.vehicle_id && s.vehicle) {
          vehicleLabelById[s.vehicle_id] =
            `${(s.vehicle as any).brand ?? ''} ${(s.vehicle as any).model ?? ''}`.trim() ||
            `Veículo #${s.vehicle_id}`;
          if ((s.vehicle as any).plate) {
            vehiclePlateById[s.vehicle_id] = String((s.vehicle as any).plate);
          }
        }
      });

      // Todas as despesas do veículo (não só as do período) — para discriminar o lucro da venda
      const expensesByVehicleId: Record<number, Expense[]> = {};
      (expenses || []).forEach((e: Expense) => {
        const vId = Number((e as any).vehicle_id || 0);
        if (!vId) return;
        if (!expensesByVehicleId[vId]) expensesByVehicleId[vId] = [];
        expensesByVehicleId[vId].push(e);
      });

      const categoryLabel = (cat: string) =>
        EXPENSE_CATEGORIES[cat as ExpenseCategory] || cat || 'Outros';

      // Veículos vendidos = data da venda no período
      const salesSoldInPeriod = salesFiltered;

      const salesDetail: DreSaleDetailRow[] = salesSoldInPeriod.map((s: Sale) => {
        const vehicle = s.vehicle as any;
        const salePrice = toNum((s as any).sale_price);
        const isCons = !!vehicle?.is_consignado;
        const receitaEmpresa = getCompanyRevenueFromSale(s);
        const purchasePrice = isCons ? 0 : toNum(vehicle?.purchase_price);
        const vId = Number((s as any).vehicle_id || 0);
        const vehicleExpenses = expensesByVehicleId[vId] || [];
        const expenseItems: DreSaleExpenseItem[] = vehicleExpenses.map((e) => ({
          id: Number((e as any).id),
          expense_date: (e as any).expense_date || '',
          category: String((e as any).category ?? ''),
          category_label: categoryLabel(String((e as any).category ?? '')),
          description: String((e as any).description ?? ''),
          amount: toNum((e as any).amount),
        }));
        const vExpenses = expenseItems.reduce((sum, item) => sum + item.amount, 0);
        const lucroOperacional = receitaEmpresa - purchasePrice - vExpenses;
        const lucroEstimado = getCashProfitInRangeForSale(s, dateFrom, dateTo);
        const vLabel =
          vehicleLabelById[vId] ??
          (vehicle ? `${vehicle.brand ?? ''} ${vehicle.model ?? ''}`.trim() : '') ??
          '';

        return {
          id: Number((s as any).id),
          sale_date: (s as any).sale_date || '',
          vehicle_id: vId,
          vehicle_label: vLabel || (vId ? `Veículo #${vId}` : '—'),
          plate: vehiclePlateById[vId] || vehicle?.plate || undefined,
          is_consignado: isCons,
          sale_price: salePrice,
          receita_empresa: receitaEmpresa,
          custo_compra: purchasePrice,
          despesas_veiculo: vExpenses,
          lucro_operacional: lucroOperacional,
          lucro_estimado: lucroEstimado,
          expense_items: expenseItems,
        };
      });

      const expensesDetail: DreExpenseDetailRow[] = expensesFiltered.map((e: Expense) => {
        const vId = Number((e as any).vehicle_id || 0);
        const vLabel =
          (e as any).vehicle
            ? `${(e as any).vehicle?.brand ?? ''} ${(e as any).vehicle?.model ?? ''}`.trim()
            : (vehicleLabelById[vId] ?? (vId ? `Veículo #${vId}` : '—'));
        return {
          id: Number((e as any).id),
          expense_date: (e as any).expense_date || '',
          vehicle_label: vLabel || (vId ? `Veículo #${vId}` : '—'),
          category: categoryLabel(String((e as any).category ?? '')),
          description: String((e as any).description ?? ''),
          amount: toNum((e as any).amount),
          affects_store_cash: expenseAffectsStoreCash(e),
        };
      });

      const movementsDetail: DreMovementDetailRow[] = movFiltered.map((m: MovimentacaoCaixa) => ({
        id: String((m as any).id),
        date: (m as any).date || '',
        type: (m as any).type,
        category: String((m as any).category ?? ''),
        description: String((m as any).description ?? ''),
        amount: toNum((m as any).amount),
        affects_cash: movimentacaoAfetaSaldoCaixa(m),
        payable_status: (m as any).payableStatus ?? (m as any).payable_status,
        due_date: (m as any).dueDate ?? (m as any).due_date,
      }));

      setDetailSales(salesDetail);
      setDetailExpenses(expensesDetail);
      setDetailMovements(movementsDetail);

      // Faturamento bruto = soma dos valores de venda (para referência)
      const faturamentoBrutoVendas = Number(salesFiltered.reduce((sum, s) => Number(sum) + toNum(s.sale_price), 0));
      const receitaVendas = Number(sumCashRevenueInRange(salesForCash, dateFrom, dateTo));
      const lucroRealVendas = Number(sumCashProfitInRange(salesForCash, dateFrom, dateTo));
      const soldVehicleIdsInPeriod = new Set(
        salesWithCashInPeriod
          .map((s: Sale) => Number((s as { vehicle_id?: unknown }).vehicle_id || 0))
          .filter((id: number) => id > 0)
      );
      const receitaMovimentacoes = Number(movFiltered
        .filter((m: MovimentacaoCaixa) => m.type === 'entrada')
        .reduce((sum, m) => Number(sum) + toNum(m.amount), 0));
      const totalReceitas = Number(lucroRealVendas) + Number(receitaMovimentacoes);

      const totalDespesas = Number(
        expensesFiltered.reduce((sum, e) => {
          if (!expenseAffectsStoreCash(e)) return Number(sum);
          const vid = Number((e as { vehicle_id?: unknown }).vehicle_id || 0);
          if (vid > 0 && soldVehicleIdsInPeriod.has(vid)) return Number(sum);
          return Number(sum) + toNum(e.amount);
        }, 0)
      );
      const totalSaidasMovimentacoes = Number(movFiltered
        .filter((m: MovimentacaoCaixa) => m.type === 'saida' && movimentacaoAfetaSaldoCaixa(m))
        .reduce((sum, m) => Number(sum) + toNum(m.amount), 0));
      const totalGastos = Number(totalDespesas) + Number(totalSaidasMovimentacoes);

      const totalTaxas = Number(taxEntries.reduce((sum, e) => Number(sum) + toNum(e.amount), 0));
      const resultado = Number(totalReceitas) - Number(totalGastos) - Number(totalTaxas);

      setDreResult({
        faturamentoBrutoVendas,
        receitaVendas,
        lucroRealVendas,
        receitaMovimentacoes,
        totalReceitas,
        totalDespesas,
        totalSaidasMovimentacoes,
        totalGastos,
        totalTaxas,
        resultado,
        veiculosVendidos: salesSoldInPeriod.length,
      });
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar dados para a DRE. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const kpiCardSx = {
    p: 2.5,
    height: '100%',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
  };

  const handleDownloadPdf = async () => {
    if (!dreResult) return;
    setDownloadingPdf(true);
    try {
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;
      const doc = (
        <DrePDFDoc
          dreResult={dreResult}
          dateFrom={dateFrom}
          dateTo={dateTo}
          taxEntries={taxEntries}
          logoUrl={logoUrl}
          showDetailed={showDetailed}
          detailSales={detailSales}
          detailExpenses={detailExpenses}
          detailMovements={detailMovements}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DRE-R-Motors-${dateFrom}-a-${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Erro ao gerar PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!dreResult) return;
    setDownloadingExcel(true);
    try {
      const generatedAtLabel = new Date().toLocaleString('pt-BR');
      await exportDreReportExcel({
        dreResult,
        dateFrom,
        dateTo,
        taxEntries,
        generatedAtLabel,
      });
    } catch (err: any) {
      setError(err?.message || 'Erro ao gerar Excel.');
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            DRE
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 900 }}>
            Demonstração do Resultado no período (regime de caixa): lucro e receita conforme pagamentos recebidos. Vendas a prazo entram na entrada e nas datas de baixa das parcelas. Despesas de veículos vendidos no período não são subtraídas de novo.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? 'Gerando...' : 'Gerar DRE'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={downloadingExcel ? <CircularProgress size={18} color="inherit" /> : <GridOnIcon />}
            onClick={handleDownloadExcel}
            disabled={downloadingExcel || !dreResult}
          >
            {downloadingExcel ? 'Gerando Excel...' : 'Exportar Excel'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={downloadingPdf ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || !dreResult}
          >
            {downloadingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Período */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Período
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                type="date"
                label="Data inicial"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                type="date"
                label="Data final"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Taxas e impostos (cadastro manual) */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Taxas e impostos (manual)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Valores cadastrados aqui serão deduzidos do resultado final.
            </Typography>
            {taxEntries.map((entry) => (
              <Box key={entry.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Ex.: ISS, IRPJ, taxas bancárias"
                  value={entry.description}
                  onChange={(e) => updateTaxEntry(entry.id, 'description', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="text"
                  placeholder="R$ 0,00"
                  value={formatCurrency(entry.amount)}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    const n = v === '' ? 0 : parseFloat(v) / 100;
                    updateTaxEntry(entry.id, 'amount', n);
                  }}
                  sx={{ width: 140 }}
                />
                <IconButton color="error" onClick={() => removeTaxEntry(entry.id)} title="Remover">
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addTaxEntry} size="small">
              Adicionar taxa/imposto
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {dreResult && (
        <Paper elevation={0} sx={{ mt: 3, p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Resultado — {dateFrom} a {dateTo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Regime de caixa: lucro e receita entram conforme pagamentos (vendas a prazo: entrada na venda, parcelas na baixa).
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Switch checked={showDetailed} onChange={(_, v) => setShowDetailed(v)} />}
                  label="DRE detalhada"
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: 2.5,
                mt: 0.5,
                mb: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(5, 1fr)',
                },
              }}
            >
              {[
                {
                  key: 'vendidos',
                  label: 'Veículos vendidos',
                  value: String(dreResult.veiculosVendidos),
                  hint: 'Vendas com data no período.',
                  color: undefined as string | undefined,
                },
                {
                  key: 'receitas',
                  label: 'Total de receitas',
                  value: formatCurrency(dreResult.totalReceitas),
                  hint: 'Lucro recebido no período + entradas de movimentação.',
                  color: undefined,
                },
                {
                  key: 'gastos',
                  label: 'Total de gastos',
                  value: formatCurrency(dreResult.totalGastos),
                  hint: 'Despesas (exceto veículos vendidos no período) + saídas de caixa.',
                  color: undefined,
                },
                {
                  key: 'taxas',
                  label: 'Taxas / impostos',
                  value: formatCurrency(dreResult.totalTaxas),
                  hint: 'Valores manuais cadastrados.',
                  color: undefined,
                },
                {
                  key: 'resultado',
                  label: 'Resultado',
                  value: formatCurrency(dreResult.resultado),
                  hint: 'Receitas − gastos − taxas.',
                  color: dreResult.resultado >= 0 ? 'success.main' : 'error.main',
                },
              ].map((kpi) => (
                <Paper key={kpi.key} elevation={0} sx={kpiCardSx}>
                  <Typography variant="overline" color="text.secondary">
                    {kpi.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: kpi.color }}>
                    {kpi.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {kpi.hint}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Veículos vendidos no período — sempre visível */}
            <Paper
              elevation={0}
              sx={{ mb: 2.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Veículos vendidos no período ({dreResult.veiculosVendidos})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Lucro de cada venda discriminando custo de compra e gastos do próprio veículo.
              </Typography>
              {detailSales.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma venda no período selecionado.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {detailSales.map((r) => (
                    <Accordion key={r.id} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', width: '100%', pr: 1 }}>
                          <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                            {r.vehicle_label}
                            {r.plate ? ` · ${r.plate}` : ''}
                          </Typography>
                          <Chip size="small" label={r.is_consignado ? 'Consignado' : 'Direto'} variant="outlined" />
                          <Typography variant="body2" color="text.secondary">
                            {(r.sale_date || '').split('T')[0]}
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={r.lucro_operacional >= 0 ? 'success.main' : 'error.main'}
                          >
                            Lucro {formatCurrency(r.lucro_operacional)}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1} sx={{ mb: 1.5 }}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Venda</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatCurrency(r.sale_price)}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Receita (empresa)</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatCurrency(r.receita_empresa)}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Custo de compra</Typography>
                            <Typography variant="body2">{formatCurrencyDeduction(r.custo_compra)}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Lucro no caixa (período)</Typography>
                            <Typography variant="body2" fontWeight={600} color={r.lucro_estimado >= 0 ? 'success.main' : 'error.main'}>
                              {formatCurrency(r.lucro_estimado)}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Gastos do veículo ({formatCurrencyDeduction(r.despesas_veiculo)})
                        </Typography>
                        {r.expense_items.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Sem despesas cadastradas neste veículo.</Typography>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Data</TableCell>
                                  <TableCell>Categoria</TableCell>
                                  <TableCell>Descrição</TableCell>
                                  <TableCell align="right">Valor</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {r.expense_items.map((exp) => (
                                  <TableRow key={exp.id}>
                                    <TableCell>{(exp.expense_date || '').split('T')[0]}</TableCell>
                                    <TableCell>{exp.category_label}</TableCell>
                                    <TableCell>{exp.description || '—'}</TableCell>
                                    <TableCell align="right">{formatCurrencyDeduction(exp.amount)}</TableCell>
                                  </TableRow>
                                ))}
                                <TableRow>
                                  <TableCell colSpan={3}><strong>Total despesas do veículo</strong></TableCell>
                                  <TableCell align="right"><strong>{formatCurrencyDeduction(r.despesas_veiculo)}</strong></TableCell>
                                </TableRow>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                  <TableCell colSpan={3}>
                                    <strong>Lucro operacional</strong>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Receita − custo − despesas
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography component="span" fontWeight={700} color={r.lucro_operacional >= 0 ? 'success.main' : 'error.main'}>
                                      {formatCurrency(r.lucro_operacional)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 560 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor (R$)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Receitas</strong></TableCell>
                    <TableCell align="right" />
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 3 }}>Faturamento bruto (valor dos veículos)</TableCell>
                    <TableCell align="right">{formatCurrency(dreResult.faturamentoBrutoVendas)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 3 }}>Receita recebida (vendas no período)</TableCell>
                    <TableCell align="right">{formatCurrency(dreResult.receitaVendas)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 3 }}>Lucro recebido (vendas no período)</TableCell>
                    <TableCell align="right">{formatCurrency(dreResult.lucroRealVendas)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 3 }}>Entradas de movimentação de caixa</TableCell>
                    <TableCell align="right">{formatCurrency(dreResult.receitaMovimentacoes)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total receitas</strong></TableCell>
                    <TableCell align="right">{formatCurrency(dreResult.totalReceitas)}</TableCell>
                  </TableRow>
                  <TableRow><TableCell colSpan={2} sx={{ py: 0 }}><Divider /></TableCell></TableRow>
                  <TableRow>
                    <TableCell><strong>Despesas / Gastos</strong></TableCell>
                    <TableCell align="right" />
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 3 }}>Despesas de veículos</TableCell>
                    <TableCell align="right">{formatCurrencyDeduction(dreResult.totalDespesas)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 3 }}>Saídas de movimentação de caixa</TableCell>
                    <TableCell align="right">{formatCurrencyDeduction(dreResult.totalSaidasMovimentacoes)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total gastos</strong></TableCell>
                    <TableCell align="right">{formatCurrencyDeduction(dreResult.totalGastos)}</TableCell>
                  </TableRow>
                  {dreResult.totalTaxas > 0 && (
                    <>
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}><strong>Taxas e impostos (manual)</strong></TableCell>
                        <TableCell align="right" />
                      </TableRow>
                      {taxEntries
                        .filter((e) => toNum(e.amount) > 0)
                        .map((e) => (
                          <TableRow key={e.id}>
                            <TableCell sx={{ pl: 4 }}>{e.description || '(Sem descrição)'}</TableCell>
                            <TableCell align="right">{formatCurrencyDeduction(e.amount)}</TableCell>
                          </TableRow>
                        ))}
                      <TableRow>
                        <TableCell sx={{ pl: 3 }}>Total taxas e impostos</TableCell>
                        <TableCell align="right">{formatCurrencyDeduction(dreResult.totalTaxas)}</TableCell>
                      </TableRow>
                    </>
                  )}
                  <TableRow><TableCell colSpan={2} sx={{ py: 0 }}><Divider /></TableCell></TableRow>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell><strong>Resultado (Lucro / Prejuízo)</strong></TableCell>
                    <TableCell align="right">
                      <Typography component="span" color={dreResult.resultado >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                        {formatCurrency(dreResult.resultado)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {showDetailed && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Modo detalhado ativo: o PDF incluirá veículos vendidos (com discriminação de gastos), despesas e movimentações do período.
                </Alert>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">Despesas detalhadas (veículos) — período</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Veículo</TableCell>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor</TableCell>
                            <TableCell>Caixa</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailExpenses.length === 0 ? (
                            <TableRow><TableCell colSpan={6} align="center">Sem despesas no período.</TableCell></TableRow>
                          ) : (
                            detailExpenses.map((r) => (
                              <TableRow key={r.id} hover>
                                <TableCell>{(r.expense_date || '').split('T')[0]}</TableCell>
                                <TableCell>{r.vehicle_label}</TableCell>
                                <TableCell>{r.category}</TableCell>
                                <TableCell>{r.description}</TableCell>
                                <TableCell align="right">{formatCurrencyDeduction(r.amount)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={r.affects_store_cash ? 'Desconta' : 'Não desconta'}
                                    color={r.affects_store_cash ? 'default' : 'info'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">Movimentações detalhadas (caixa)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Saídas marcadas como <strong>conta a pagar pendente</strong> não entram no total de saídas do caixa até dar baixa.
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor</TableCell>
                            <TableCell>Caixa</TableCell>
                            <TableCell>Vencimento</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailMovements.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center">Sem movimentações no período.</TableCell></TableRow>
                          ) : (
                            detailMovements.map((r) => (
                              <TableRow key={r.id} hover>
                                <TableCell>{(r.date || '').split('T')[0]}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={r.type === 'entrada' ? 'Entrada' : 'Saída'} color={r.type === 'entrada' ? 'success' : 'error'} variant="outlined" />
                                </TableCell>
                                <TableCell>{r.category || '—'}</TableCell>
                                <TableCell>{r.description}</TableCell>
                                <TableCell align="right">
                                  {r.type === 'entrada' ? '+' : '−'} {formatCurrency(r.amount)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={r.type === 'entrada' ? 'Entra' : (r.affects_cash ? 'Debitado' : 'Pendente')}
                                    color={r.type === 'entrada' ? 'success' : (r.affects_cash ? 'default' : 'warning')}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  {r.due_date ? r.due_date : '—'}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
        </Paper>
      )}
    </Box>
  );
};

export default DrePage;
